import React from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useRealtimeMessages } from '../hooks/useMessageUpdates';

const MessageHistory: React.FC = () => {
  const { user } = useAuthStore();
  const { messages, loading, error } = useRealtimeMessages(user?.id || '');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'in_transit': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20';
      case 'pending': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'failed': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      default: return 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:text-gray-400 dark:bg-gray-700';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const sortedMessages = messages.sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Message History</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Track all your messages sent through our feathered messengers
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading your messages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Message History</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Track all your messages sent through our feathered messengers
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Unable to load messages</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Message History</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          Track all your messages sent through our feathered messengers
        </p>
      </div>

      {sortedMessages.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
          <div className="text-4xl mb-4">📬</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No messages yet</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Start your journey with Neubird by sending your first message!
          </p>
          <Link
            to="/send"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Send First Message
          </Link>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-200">
            {sortedMessages.map((message) => (
              <div key={message.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(message.status)}`}>
                        {formatStatus(message.status)}
                      </span>
                      <span className="text-sm text-gray-500">
                        {message.bird_type.name}
                      </span>
                      <span className="text-sm text-gray-500">
                        ${message.cost.toFixed(2)}
                      </span>
                    </div>
                    <div className="mb-2">
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        <span className="font-medium">To:</span> {message.destination.address || `${message.destination.city}, ${message.destination.country}`}
                      </p>
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        <span className="font-medium">From:</span> {message.origin.address || `${message.origin.city}, ${message.origin.country}`}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Sent: {new Date(message.created_at).toLocaleString()}</span>
                      {message.status === 'delivered' && message.actual_delivery && (
                        <span>Delivered: {new Date(message.actual_delivery).toLocaleString()}</span>
                      )}
                      {message.status !== 'delivered' && (
                        <span>Est. Delivery: {new Date(message.estimated_delivery).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <Link
                      to={`/messages/${message.id}`}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageHistory;
